import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import {
  Download,
  MonitorCheck,
  ShieldCheck,
  WifiOff,
  FileText,
  Truck,
  Factory,
  HardDrive,
  Cpu,
  CheckCircle2,
  ShieldAlert,
  MousePointerClick,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODULES = [
  "Dashboard",
  "Item Master",
  "Purchase Entry",
  "Raw Material Stock",
  "Finished Goods",
  "BOM / Manufacturing",
  "Production",
  "Scrap Ledger",
  "Sales & Invoicing",
  "E-Way Bill",
  "Delivery Challan",
  "Payment In / Out",
  "GST / API Settings",
  "Invoice Logo",
];

const FEATURES = [
  {
    icon: WifiOff,
    title: "Works Offline",
    desc: "Runs fully on your PC. No internet needed for daily factory & accounts work.",
  },
  {
    icon: HardDrive,
    title: "Data Stays On Your PC",
    desc: "Every entry is saved locally and stays private on your own computer.",
  },
  {
    icon: FileText,
    title: "GST Invoicing",
    desc: "GST / IGST sales & purchase, HSN, printable tax invoices with your logo.",
  },
  {
    icon: Truck,
    title: "E-Way & Challan",
    desc: "E-Way Bill records, delivery challans and transporter details in one place.",
  },
  {
    icon: Factory,
    title: "Full Factory Flow",
    desc: "Purchase → Stock → BOM → Production → Scrap → Finished Stock → Sales → Profit.",
  },
  {
    icon: MonitorCheck,
    title: "Real Desktop App",
    desc: "Installs like normal software with a desktop icon and Start Menu shortcut.",
  },
];

const STEPS = [
  {
    n: "1",
    title: "Download the installer",
    desc: "Click the download button to get the Windows setup file (.exe).",
  },
  {
    n: "2",
    title: "Run the setup",
    desc: 'Double-click the file. If Windows shows "SmartScreen", click More info → Run anyway.',
  },
  {
    n: "3",
    title: "Open from desktop",
    desc: "A SONIC SLI ERP icon appears on your desktop. Double-click it to start working.",
  },
];

function App() {
  const [info, setInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/download/info`)
      .then((r) => setInfo(r.data))
      .catch((e) => console.error("info error", e));
  }, []);

  const handleDownload = () => {
    setDownloading(true);
    window.location.href = `${API}/download/installer`;
    setTimeout(() => setDownloading(false), 4000);
  };

  return (
    <div className="sli" data-testid="download-landing">
      <div className="sli-bg-grid" />

      {/* Top bar */}
      <header className="sli-header">
        <div className="sli-brand" data-testid="brand-header">
          <img
            className="sli-logo"
            src={"/logo-sli.png"}
            alt="SONIC SLI"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div className="sli-brand-text">
            <strong>SONIC SLI ERP</strong>
            <span>STAR LITE INDUSTRIES</span>
          </div>
        </div>
        <div className="sli-platform-tag" data-testid="platform-tag">
          <Cpu size={15} /> Windows 10 / 11 · 64-bit
        </div>
      </header>

      {/* Hero */}
      <main className="sli-hero">
        <div className="sli-hero-left">
          <div className="sli-badge">Desktop Edition · v{info?.version || "1.0.0"}</div>
          <h1 className="sli-title">
            Your Factory & Accounts,<br />
            <span className="sli-accent">now a real Windows app.</span>
          </h1>
          <p className="sli-sub">
            SONIC SLI ERP installs like proper software — with a desktop icon,
            Start Menu shortcut and offline data. Purchases, production, GST
            invoicing, E-Way bills and profit tracking, all in one place.
          </p>

          <div className="sli-cta-row">
            <button
              className="sli-download-btn"
              onClick={handleDownload}
              disabled={info && !info.available}
              data-testid="download-installer-btn"
            >
              <Download size={22} />
              {downloading ? "Starting download…" : "Download for Windows"}
            </button>
            <div className="sli-file-meta" data-testid="file-meta">
              <span>{info?.filename || "SONIC-SLI-ERP-Setup-1.0.0.exe"}</span>
              <span className="sli-dot">•</span>
              <span>{info?.size_mb ? `${info.size_mb} MB` : "~78 MB"}</span>
            </div>
          </div>

          <div className="sli-trust">
            <span><ShieldCheck size={15} /> No account needed</span>
            <span><WifiOff size={15} /> Runs offline</span>
            <span><HardDrive size={15} /> Data stays local</span>
          </div>
        </div>

        {/* App mockup card */}
        <div className="sli-hero-right" data-testid="app-preview">
          <div className="sli-window">
            <div className="sli-window-top">
              <span className="sli-dot-r" style={{ background: "#ff5f57" }} />
              <span className="sli-dot-r" style={{ background: "#febc2e" }} />
              <span className="sli-dot-r" style={{ background: "#28c840" }} />
              <div className="sli-window-title">SONIC SLI ERP</div>
            </div>
            <div className="sli-window-body">
              <div className="sli-appbar">
                STAR LITE INDUSTRIES
                <small>SONIC SLI ERP — Factory Management</small>
              </div>
              <div className="sli-appmain">
                <div className="sli-nav">
                  {MODULES.slice(0, 8).map((m, i) => (
                    <div key={m} className={`sli-nav-item ${i === 0 ? "on" : ""}`}>
                      {m}
                    </div>
                  ))}
                </div>
                <div className="sli-content">
                  <div className="sli-cards">
                    <div className="sli-mini-card">
                      <span>Sales</span>
                      <b>₹0</b>
                    </div>
                    <div className="sli-mini-card">
                      <span>Purchases</span>
                      <b>₹0</b>
                    </div>
                    <div className="sli-mini-card">
                      <span>Production</span>
                      <b>0</b>
                    </div>
                    <div className="sli-mini-card">
                      <span>Raw Material</span>
                      <b>0</b>
                    </div>
                  </div>
                  <div className="sli-flow">
                    Purchase → Stock → BOM → Production → Scrap → Finished → Sales → Profit
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Windows security warning help */}
      <section className="sli-section" data-testid="security-help">
        <div className="sli-warn-card">
          <div className="sli-warn-head">
            <div className="sli-warn-icon"><ShieldAlert size={24} /></div>
            <div>
              <h2 className="sli-warn-title">Windows ne warning dikhaya? Yeh normal hai.</h2>
              <p className="sli-warn-sub">
                Edge/Windows har naye app par yeh dikhata hai jo abhi widely download
                nahi hua. Aapki file <strong>safe</strong> hai — bas open karne ke liye
                neeche ke steps follow karein.
              </p>
            </div>
          </div>

          <div className="sli-warn-steps">
            <div className="sli-warn-step">
              <span className="sli-warn-badge"><MousePointerClick size={15} /> Browser</span>
              <p>Download list mein file ke paas <strong>"…"</strong> (ya keep option) par click karke <strong>"Keep / Rakhein"</strong> chunein.</p>
            </div>
            <div className="sli-warn-step">
              <span className="sli-warn-badge"><MousePointerClick size={15} /> SmartScreen</span>
              <p>File double-click karne par blue screen aaye to <strong>"More info"</strong> → <strong>"Run anyway"</strong> par click karein.</p>
            </div>
            <div className="sli-warn-step">
              <span className="sli-warn-badge"><CheckCircle2 size={15} /> Ready</span>
              <p>Install complete — desktop par <strong>SONIC SLI ERP</strong> icon se app khulega.</p>
            </div>
          </div>

          <div className="sli-checksum" data-testid="checksum-row">
            <span className="sli-checksum-label"><ShieldCheck size={14} /> File verify (SHA-256):</span>
            <code>{info?.sha256 || "…"}</code>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="sli-section">
        <h2 className="sli-h2">Everything your unit needs</h2>
        <div className="sli-feature-grid" data-testid="feature-grid">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div className="sli-feature" key={f.title}>
                <div className="sli-feature-icon">
                  <Icon size={22} />
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Install steps */}
      <section className="sli-section sli-steps-section">
        <h2 className="sli-h2">Install in 3 easy steps</h2>
        <div className="sli-steps" data-testid="install-steps">
          {STEPS.map((s) => (
            <div className="sli-step" key={s.n}>
              <div className="sli-step-num">{s.n}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="sli-modules">
          <span className="sli-modules-label">Included modules:</span>
          {MODULES.map((m) => (
            <span className="sli-chip" key={m}>
              <CheckCircle2 size={13} /> {m}
            </span>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="sli-bottom-cta">
        <h2>Ready to run SONIC SLI ERP on your PC?</h2>
        <button
          className="sli-download-btn lg"
          onClick={handleDownload}
          disabled={info && !info.available}
          data-testid="download-installer-btn-bottom"
        >
          <Download size={22} /> Download for Windows
        </button>
        <p className="sli-bottom-meta">
          {info?.filename || "SONIC-SLI-ERP-Setup-1.0.0.exe"} ·{" "}
          {info?.size_mb ? `${info.size_mb} MB` : "~78 MB"} · Windows 64-bit
        </p>
      </section>

      <footer className="sli-footer">
        <span>© 2026 STAR LITE INDUSTRIES — SONIC SLI ERP</span>
        <span>Desktop Edition v{info?.version || "1.0.0"}</span>
      </footer>
    </div>
  );
}

export default App;
